import {
    collection,
    documentId,
    getCountFromServer,
    getDocs,
    limit,
    orderBy,
    query,
    QueryConstraint,
    startAfter,
    where,
} from "firebase/firestore";
import { Collection } from "intake_shared/src/enum";
import {
    IMasterListSearchByOptions,
    MasterListStatus,
} from "intake_shared/src/types";
import { dbv9 } from "../utils/firestore";
import { getPhoneNumberSearchVariations, getStringVariations } from "../utils/utils";
import { getClientFromStoreData } from "./util";

export interface MasterListPageCursor {
    id: string;
    dateOfBirth?: Date;
}

export const MASTER_LIST_PAGE_SIZE = 100;

export async function searchMasterListPage(
    status: MasterListStatus,
    options: IMasterListSearchByOptions,
    cursor?: MasterListPageCursor,
    knownTotal?: number
) {
    const tags = Array.from(new Set(options.tagKeys ?? []));
    if (!tags.length) throw new Error("Choose a tag to search.");
    const collections =
        status === "ALL"
            ? [Collection.CLIENTS, Collection.HISTORY]
            : status === "active"
            ? [Collection.CLIENTS]
            : [Collection.HISTORY];
    const field = options.email
        ? "email"
        : options.firstname
        ? "first_name"
        : options.lastname
        ? "last_name"
        : options.phoneno
        ? "phone_no"
        : undefined;
    const value =
        field === "email"
            ? options.email.trim()
            : field === "first_name"
            ? options.firstname
            : field === "last_name"
            ? options.lastname
            : options.phoneno;
    const values = field
        ? Array.from(
              new Set(
                  field === "phone_no"
                      ? getPhoneNumberSearchVariations(value!)
                      : getStringVariations(value!)
              )
          )
        : [];
    const perQuery = Math.max(1, Math.floor(30 / tags.length));
    const groups: string[][] = [];
    for (let index = 0; index < values.length; index += perQuery)
        groups.push(values.slice(index, index + perQuery));
    if (!groups.length) groups.push([]);
    const byBirthDate = !field && Boolean(options.date_of_birth);
    const queries = collections.flatMap((name) =>
        groups.map((group) => {
            const filters: QueryConstraint[] = [
                where("active_tag_keys", "array-contains-any", tags),
            ];
            if (field) filters.push(where(field, "in", group));
            if (byBirthDate) {
                const date = options.date_of_birth!;
                filters.push(
                    where("date_of_birth", ">=", date),
                    where("date_of_birth", "<", new Date(date.getTime() + 86400000))
                );
            }
            return query(collection(dbv9, name), ...filters);
        })
    );
    const [pages, total] = await Promise.all([
        Promise.all(
            queries.map((base) => {
                const ordering = byBirthDate
                    ? [orderBy("date_of_birth"), orderBy(documentId())]
                    : [orderBy(documentId())];
                const after = cursor
                    ? [
                          byBirthDate
                              ? startAfter(cursor.dateOfBirth, cursor.id)
                              : startAfter(cursor.id),
                      ]
                    : [];
                return getDocs(
                    query(
                        base,
                        ...ordering,
                        ...after,
                        limit(MASTER_LIST_PAGE_SIZE + 1)
                    )
                );
            })
        ),
        knownTotal === undefined
            ? Promise.all(queries.map((base) => getCountFromServer(base))).then(
                  (counts) =>
                      counts.reduce((sum, count) => sum + count.data().count, 0)
              )
            : Promise.resolve(knownTotal),
    ]);
    const documents = pages
        .flatMap((page) => page.docs)
        .sort((left, right) => {
            if (byBirthDate) {
                const difference =
                    left.get("date_of_birth").toMillis() -
                    right.get("date_of_birth").toMillis();
                if (difference) return difference;
            }
            return left.id < right.id ? -1 : left.id > right.id ? 1 : 0;
        });
    const selected = documents.slice(0, MASTER_LIST_PAGE_SIZE);
    const last = selected[selected.length - 1];
    const nextCursor: MasterListPageCursor | undefined =
        documents.length > MASTER_LIST_PAGE_SIZE && last
            ? {
                  id: last.id,
                  dateOfBirth: byBirthDate
                      ? last.get("date_of_birth").toDate()
                      : undefined,
              }
            : undefined;
    return {
        clients: selected.map((doc) =>
            getClientFromStoreData({ ...doc.data(), id: doc.id })
        ),
        total,
        nextCursor,
    };
}
